import React, { memo, useRef } from "react";
import Box from "@material-ui/core/Box";
import classnames from "classnames";
import { shallowEqual } from "react-redux";
import StreamStyles from "../styles/StreamStyles";
import StreamVideoPlayer from "./StreamVideoPlayer";
import { isDefined } from "../../../util/object-util";

const arePropsEqual = (prevProps, nextProps) => {
	const propsChanged =
		prevProps.streamId !== nextProps.streamId ||
		prevProps.timeSkip !== nextProps.timeSkip ||
		prevProps.streamSrc !== nextProps.streamSrc ||
		!shallowEqual(prevProps.snapshot, nextProps.snapshot);

	return !propsChanged;
};

const StreamVideo = memo((props) => {
	const {
		streamSrc,
		streamId,
		setVideoPlayer,
		captureStreamSnapshot,
		snapshot,
		timeSkip,
		resetTimeSkip
	} = props;

	const classes = StreamStyles();

	const snapshotRef = useRef(null);
	snapshotRef.current = snapshot;

	const timeSkipRef = useRef(null);
	timeSkipRef.current = timeSkip;

	const m3u8Type = "application/x-mpegURL";
	const ytType = "video/youtube";

	const getType = () => {
		if (/.*[.]{1}m3u8.*$/.test(streamSrc)) {
			return m3u8Type;
		}
		if (/.*youtube[.].*$/.test(streamSrc)) {
			return ytType;
		}
		return "video/mp4";
	};

	const getStreamUrl = () => {
		if (!/^(https?:\/\/)/i.test(streamSrc)) {
			return `https://${streamSrc}`;
		}
		return streamSrc;
	};

	const options = {
		liveui: true,
		aspectRatio: "16:9",
		autoplay: getType() === m3u8Type,
		muted: true,
		controls: true,
		fluid: true,
		sources: [
			{
				src: getStreamUrl(),
				type: getType(),
				id: streamId
			}
		]
	};

	const onReady = (player) => {
		player.on("dispose", () => {
			captureStreamSnapshot(player);
		});

		player.on("loadedmetadata", () => {
			if (isDefined(timeSkipRef.current)) {
				if (Math.floor(player.currentTime()) !== Math.floor(timeSkipRef.current)) {
					player.currentTime(timeSkipRef.current);
				} else {
					resetTimeSkip();
					player.play();
					captureStreamSnapshot(player);
				}
			} else {
				const currentSnapshot = snapshotRef.current;
				if (currentSnapshot) {
					player.volume(currentSnapshot.volume);
					if (currentSnapshot.currentTime) {
						player.currentTime(currentSnapshot.currentTime);
					}
					if (!currentSnapshot.paused && player.paused()) {
						player.play();
					}
					if (currentSnapshot.paused && !player.paused()) {
						player.pause();
					}
				}
				Object.assign(player, { hasMetadataLoaded: true });
				captureStreamSnapshot(player);
			}
		});

		setVideoPlayer(player);
	};

	return (
		<Box
			className={classnames(classes.streamVideoContainer, {
				[classes.streamVideoContainerYt]: getType() === ytType
			})}
		>
			<StreamVideoPlayer
				options={options}
				onReady={onReady}
				onUpdateSrc={captureStreamSnapshot}
			/>
		</Box>
	);
}, arePropsEqual);

export default StreamVideo;
